/**
 * Airline ICAO code → logo URL map.
 *
 * The map is served by the backend (`AircraftDataService.getAirlineLogoMap`,
 * alias-resolved so `ROU` already points at Air Canada's icon) and cached in
 * localStorage so a warm start renders logos synchronously. Consumers that
 * render logos call `useAirlineLogoMapVersion()` so they re-render when the
 * map arrives or is refreshed after an admin edit.
 *
 * There is no bundled fallback: the backend map is the single source of truth
 * (logos are managed under Admin → Reference Data → Airlines). On a first-ever
 * cold start the boot gate waits up to 2.5 s for the map; until it lands,
 * consumers render their text fallback.
 */
import { useSyncExternalStore } from 'react';
import { debugWarn } from './debug';
import { getAirlineLogoMap } from './httpApi';

const STORAGE_KEY = 'aviotrack.airlineLogoMap.v1';
const STORAGE_KEY_DARK = 'aviotrack.airlineLogoMapDark.v1';

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let runtimeMap = readCachedMap();
// Artwork for dark backgrounds (the live-map label chip). Sparse: only airlines
// whose default logo is unreadable on dark (NetJets' grey wordmark) have one;
// everything else falls back to the default map.
let runtimeMapDark = readCachedMap(STORAGE_KEY_DARK);
let version = 0;
const listeners = new Set();
let loadPromise = null;

function readCachedMap(key = STORAGE_KEY) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (e) {
    return {};
  }
}

function writeCachedMap(map, key = STORAGE_KEY) {
  try {
    window.localStorage.setItem(key, JSON.stringify(map));
  } catch (e) {
    // Quota / private mode — the in-memory map still works for this session.
  }
}

function notify() {
  version++;
  listeners.forEach(listener => listener(version));
}

function lookup(code) {
  return runtimeMap[code] || null;
}

function normaliseMap(logos) {
  const next = {};
  if (logos && typeof logos === 'object') {
    Object.keys(logos).forEach(code => {
      const url = logos[code];
      if (code && url) next[String(code).toUpperCase()] = String(url);
    });
  }
  return next;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Replace the runtime maps. Keys are upper-cased; falsy URLs are dropped.
 * @param {Object<string,string>} logos - `{ ICAO: url }`
 * @param {Object<string,string>} [logosDark] - dark-background variants, `{ ICAO: url }`
 */
export function setRuntimeAirlineLogoMap(logos, logosDark = {}) {
  runtimeMap = normaliseMap(logos);
  runtimeMapDark = normaliseMap(logosDark);
  writeCachedMap(runtimeMap);
  writeCachedMap(runtimeMapDark, STORAGE_KEY_DARK);
  notify();
}

/** Subscribe to map changes; returns an unsubscribe function. */
export function subscribeToAirlineLogoMap(callback) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

/** Monotonic counter bumped on every setRuntimeAirlineLogoMap. */
export function getAirlineLogoMapVersion() {
  return version;
}

/** React hook: re-renders the caller whenever the logo map changes. */
export function useAirlineLogoMapVersion() {
  return useSyncExternalStore(subscribeToAirlineLogoMap, getAirlineLogoMapVersion, getAirlineLogoMapVersion);
}

/** True when a previous session left a map in localStorage (warm start). */
export function hasCachedAirlineLogoMap() {
  return Object.keys(runtimeMap).length > 0;
}

/**
 * Fetch the map from the backend and install it. Errors are logged, never
 * thrown — a failed load leaves whatever map is already active in place.
 * Concurrent calls share one in-flight request.
 * @returns {Promise<boolean>} true when a map was installed
 */
export function loadAirlineLogoMap() {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    try {
      const resp = await getAirlineLogoMap();
      if (resp && resp.logos && typeof resp.logos === 'object') {
        setRuntimeAirlineLogoMap(resp.logos, resp.logosDark);
        return true;
      }
      debugWarn('Airline logo map response had no logos:', resp);
      return false;
    } catch (err) {
      debugWarn('Failed to load airline logo map:', err);
      return false;
    } finally {
      loadPromise = null;
    }
  })();
  return loadPromise;
}

/**
 * Get the logo URL for an airline ICAO code
 * @param {string} icaoCode - Airline ICAO code (e.g., 'DLH', 'AFR')
 * @param {string} type - 'icon' (default, light table rows) or 'map' (the dark
 *   map-label chip: the airline's dark variant when it has one, else the icon)
 * @returns {string|null} - Logo URL or null if not found
 */
export function getAirlineLogo(icaoCode, type = 'icon') {
  if (!icaoCode) return null;
  const code = String(icaoCode).toUpperCase();
  if (type === 'map' && runtimeMapDark[code]) return runtimeMapDark[code];
  return lookup(code);
}

/**
 * Check if an airline logo is available
 * @param {string} icaoCode - Airline ICAO code
 * @returns {boolean}
 */
export function hasAirlineLogo(icaoCode) {
  if (!icaoCode) return false;
  return lookup(String(icaoCode).toUpperCase()) !== null;
}

// Re-export military logo functions from the dedicated cache module
export {
  hasMilitaryLogo,
  getMilitaryLogo,
  detectServiceFromOperator,
  markServiceIconUnavailable
} from './militaryIconCache';
